import { useEffect, useId, useRef, useState } from 'react';
import { NavIcon } from '@/components/icons/NavIcon';
import type { FilterOption } from './FilterSelect';
import styles from './FilterMultiSelect.module.css';

// 빈 선택은 필터 해제(미지정 값까지 포함한 전체)다. 항목 선택 시 팝업을 유지한다.
// align='right'는 화면 오른쪽 끝에 놓인 필터용 — 팝업이 뷰포트 밖으로 잘리지 않게 트리거의 오른쪽에 맞춘다.
// searchable은 옵션이 많은 필터용 — 팝업 상단에 검색창을 두고 라벨 부분일치(대소문자 무시)로 좁힌다.
// 필터가 아니라 값 고르기에 쓸 때는 빈 선택이 '전체'가 아니다 — emptyLabel·clearLabel·hint 로 문구를 바꾼다.
// 기본으로 모두 체크해 두는 필터는 onClear(맨 위 버튼 동작 — 예: 전체 선택·해제)와 allSelectedLabel(모두 체크일 때 요약)을 준다.
export function FilterMultiSelect({
  label, values, options, onChange, align = 'left', searchable = false,
  emptyLabel = '전체', clearLabel = '전체 보기', hint = '선택하지 않으면 전체 표시', onClear, allSelectedLabel,
}: {
  label: string; values: string[]; options: FilterOption[]; onChange: (next: string[]) => void;
  align?: 'left' | 'right'; searchable?: boolean;
  emptyLabel?: string; clearLabel?: string; hint?: string;
  onClear?: () => void; allSelectedLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  // 닫히면 검색어를 비워 다음에 열 때 전체 목록부터 보이게 한다.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  // 요약: 전체 / 하나만 빠지면 "X 제외" / 그 외 선택 항목 나열.
  const selected = options.filter(o => values.includes(o.value));
  const excluded = options.filter(o => !values.includes(o.value));
  const summary = !values.length
    ? emptyLabel
    : allSelectedLabel && !excluded.length
      ? allSelectedLabel
    : selected.length > 1 && excluded.length === 1 && selected.length === options.length - 1
      ? `${excluded[0].label} 제외`
      : selected.map(o => o.label).join(', ');
  const q = query.trim().toLowerCase();
  const visible = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
  return <div ref={root} className={styles.wrap}
    onBlur={event => {
      // label의 글자·여백 클릭은 체크박스에 전달되기 전에 잠시 포커스를 잃는다.
      // 실제 외부 포커스 이동만 닫고, 외부 클릭은 pointerdown에서 처리한다.
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }}
    onKeyDown={event => {
      if (event.key === 'Escape' && open) { setOpen(false); trigger.current?.focus(); }
    }}>
    <button type="button" ref={trigger} className={styles.trigger} aria-expanded={open}
      aria-controls={id} onClick={() => setOpen(value => !value)} title={summary}>
      <span className={styles.label}>{label}</span>
      <span className={styles.summary}>{summary}</span>
      <span className={styles.chev} aria-hidden><NavIcon name="chev-d" size={13} /></span>
    </button>
    {open && <div id={id} className={`${styles.popup} ${align === 'right' ? styles.popupRight : ''}`} role="group" aria-label={`${label} 필터`}>
      {searchable && <input type="search" className={styles.search} value={query} autoFocus
        placeholder={`${label} 검색`} aria-label={`${label} 검색`}
        onChange={event => setQuery(event.target.value)} />}
      <button type="button" className={styles.clear} onClick={() => (onClear ? onClear() : onChange([]))}>{clearLabel}</button>
      {searchable && q && !visible.length && <span className={styles.hint}>검색 결과 없음</span>}
      {visible.map(option => <label key={option.value} className={styles.option}>
        <input type="checkbox" checked={values.includes(option.value)} onChange={event => {
          const selected = new Set(values);
          if (event.target.checked) selected.add(option.value); else selected.delete(option.value);
          onChange(options.filter(o => selected.has(o.value)).map(o => o.value));
        }} />
        <span>{option.label}</span>
      </label>)}
      <span className={styles.hint}>{hint}</span>
    </div>}
  </div>;
}
